import { Injectable } from '@angular/core';
import { IndexType } from '@fnModels/algolia/algolia';
import { AlgoliaMatter } from '@fnModels/algolia/matter';
import { BusinessUserRole } from '@models/businesses';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { getIndex } from '@state/utils/helper';
import { AlgoliaSearchOptions } from '@state/utils/models';
import { AlgoliaBaseState, AlgoliaSearchState } from '..';
import * as actions from './matter-search.actions';

export type MatterSearchStateModel = AlgoliaBaseState<AlgoliaMatter>;

type CTX = StateContext<MatterSearchStateModel>;

@State<MatterSearchStateModel>({
	name: 'matterSearch',
	defaults: {
		searchTerm: '',
		searching: false,
		success: null,
		error: null,
		items: [],
	},
})
@Injectable()
export class MatterSearchState extends AlgoliaSearchState<MatterSearchStateModel> {
	protected indexName = getIndex(IndexType.MATTERS);

	@Selector([MatterSearchState])
	static getList(state: MatterSearchStateModel): unknown {
		return state?.items;
	}

	@Selector([MatterSearchState])
	static getSearching(state: MatterSearchStateModel): boolean {
		return state?.searching;
	}

	@Action(actions.Search)
	async search(ctx: CTX, { payload }: actions.Search) {
		const facetFilters = [];
		const restrictSearchableAttributes: string[] = [];
		const requestOptions: AlgoliaSearchOptions = {};

		if (payload?.filters?.fileTypes?.length > 0) {
			const fileTypeFilter = payload.filters.fileTypes?.map(
				(x) => `fileType:${x}`,
			);
			facetFilters.push(fileTypeFilter);
		}

		if (payload?.filters?.filterByNumber) {
			restrictSearchableAttributes.push('number');
			requestOptions.disableTypoToleranceOnAttributes = ['number'];
		}

		if (this.user.restricted) {
			const facetType =
				this.user.role === BusinessUserRole.LAWYER ? 'lawyerId' : 'clerkId';
			facetFilters.push(`${facetType}:${this.user.id}`);
		}

		if (facetFilters?.length > 0) {
			requestOptions.facetFilters = facetFilters;
		}

		if (restrictSearchableAttributes.length > 0) {
			requestOptions.restrictSearchableAttributes =
				restrictSearchableAttributes;
		}

		await this.algoliaSearchHandler(ctx, payload.searchTerm, requestOptions);
	}

	@Action(actions.ClearResults)
	clear({ patchState }: CTX) {
		patchState({ searching: false, searchTerm: null, items: [] });
	}

	@Action(actions.ClearCache)
	async clearCache(): Promise<void> {
		await this.algoliaSearchClient.clearCache();
	}
}
